import React, { useContext, useState } from 'react';
import { batch } from 'react-redux';

import { Separator } from '@r1-frontend/ui-react/components/Separator';
import {
    BlockContainer,
    ListContainer,
} from '@r1-frontend/ui-react/experimental/containers';
import { SwitchableSubscription } from '@r1-frontend/ui-react/organisms/TariffSubscription';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import ChannelListPopup from '~/src/components/Bundles/ChannelListPopup';
import { flushDecoders, removeAllTvEntities } from '~/src/features/tariffBuilder/actions';
import addTvEntity from '~/src/features/tariffBuilder/actions/addTvEntity';
import removeTvEntity from '~/src/features/tariffBuilder/actions/removeTvEntity';
import { useConstructorAnalytics } from '~/src/features/tariffBuilder/analytics/hooks/useConstructorAnalytics';
import SubscriptionsGroup from '~/src/features/tariffBuilder/components/SubscriptionsGroup';
import BaseTvDisabled from '~/src/features/tariffBuilder/components/toast/BaseTvDisabled';
import { Ctx } from '~/src/features/tariffBuilder/Ctx';
import { useCtxDispatch, useCtxSelector } from '~/src/features/tariffBuilder/hooks';
import getAdditionalTv from '~/src/features/tariffBuilder/selectors/getAdditionalTv';
import getBaseTvItem from '~/src/features/tariffBuilder/selectors/getBaseTvItem';
import { EEntity } from '~/src/features/tariffBuilder/types/EEntity';

type TOwnState = {
    selectedId: undefined,
    isPopupOpen: false,
} | {
    selectedId: number,
    isPopupOpen: true,
}

const TvSubscriptionsContainer = (): JSX.Element => {
    const { additionalTvSubscriptions, baseTvItem, tv } = useCtxSelector(state => ({
        additionalTvSubscriptions: getAdditionalTv(state),
        tv: state.tv,
        baseTvItem: getBaseTvItem(state),
    }));

    const dispatch = useCtxDispatch();
    const { toaster } = useContext(Ctx);
    const [{ selectedId, isPopupOpen }, setState] = useState<TOwnState>({
        selectedId: undefined,
        isPopupOpen: false,
    });

    const analytics = useConstructorAnalytics();

    const handleSelect = (key: number, group: string, isSelected: boolean) => () => {
        if (isSelected) {
            if (group === 'base') {
                batch(() => {
                    dispatch(removeAllTvEntities());
                    dispatch(flushDecoders());
                });
                toaster.add(<BaseTvDisabled/>);
            } else {
                dispatch(removeTvEntity({
                    entity: EEntity.tv,
                    group,
                    key,
                }));
            }
        } else {
            const groupTitle = tv[group].title;
            const itemTitle = tv[group].items[key].title;

            dispatch(addTvEntity({
                entity: EEntity.tv,
                group,
                key,
                groupTitle,
                itemTitle,
            },
            analytics));
        }
    };

    const handleIconClick = (id: number) => () => {
        setState({ isPopupOpen: true, selectedId: id });
    };

    const handleClosePopup = () => {
        setState({ selectedId: undefined, isPopupOpen: false });
    };

    return <>
        <BlockContainer
            indent={{ $united: [24] }}
            rounder={{ $radius: 32 }}
        >
            <ListContainer $size={24}>
                <SwitchableSubscription
                    cost={baseTvItem.cost.fact}
                    oldCost={baseTvItem.cost.previous}
                    checked={baseTvItem.isSelected}
                    onIconClick={handleIconClick(baseTvItem.contentId)}
                    onToggle={handleSelect(baseTvItem.key, 'base', baseTvItem.isSelected)}
                    disabled={baseTvItem.disabled}
                >
                    {baseTvItem.title}
                </SwitchableSubscription>

                <Separator $color={COLORS.Outline_Light}/>

                <ListContainer $size={12}>
                    {additionalTvSubscriptions.map(tvSubscription => <SubscriptionsGroup
                        key={`TvSubscription_${tvSubscription.alias}`}
                        items={tvSubscription.items}
                        groupName={tvSubscription.alias}
                        title={tvSubscription.title}
                        icon={tvSubscription.imagePath}
                        selectedCount={tvSubscription.selectedCount}
                        onSelect={handleSelect}
                        onIconClick={(_id) => handleIconClick(_id)()}
                        disabled={!baseTvItem.isSelected}
                    />)}
                </ListContainer>
            </ListContainer>
        </BlockContainer>

        {(isPopupOpen && selectedId) && (
            <ChannelListPopup
                isOpen={isPopupOpen}
                onClose={handleClosePopup}
                freePackets={selectedId}
            />
        )}
    </>;
};

export default TvSubscriptionsContainer;
